import { useMemo } from 'react';
import { usePlants } from './usePlants';
import { isExpandedCareInfo } from '../types/careExpanded';
import type { PlantWithId } from '../types/plant';

export interface ShoppingItem {
  /** Product name/description */
  name: string;
  /** Category: soils, amendments, fertilizers */
  category: 'soils' | 'amendments' | 'fertilizers';
  /** Plants that need this product */
  plants: Array<{ id: string; name: string }>;
}

export interface ShoppingListData {
  items: ShoppingItem[];
  loading: boolean;
  error: string | null;
  /** Total unique products */
  totalProducts: number;
  /** Total plants with v2 care info */
  plantsWithCareInfo: number;
}

/**
 * Parse soil recipe ingredients to extract product names and categories
 */
function parseIngredient(ingredient: string): { name: string; category: 'soils' | 'amendments' } {
  const lower = ingredient.toLowerCase();

  // Identify if it's a base soil or an amendment
  if (
    lower.includes('potting mix') ||
    lower.includes('potting soil') ||
    lower.includes('indoor mix') ||
    lower.includes('cactus soil') ||
    lower.includes('succulent soil')
  ) {
    return { name: cleanProductName(ingredient), category: 'soils' };
  }

  // Everything else is an amendment (perlite, orchid bark, charcoal, etc.)
  return { name: cleanProductName(ingredient), category: 'amendments' };
}

/**
 * Clean up product name by removing percentage ratios
 */
function cleanProductName(raw: string): string {
  // Remove leading percentages like "50%" or "2 parts"
  return raw
    .replace(/^\d+%?\s*/i, '')
    .replace(/^\d+\s+parts?\s*/i, '')
    .trim();
}

/**
 * Normalize product names for deduplication
 */
function normalizeForComparison(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Hook to aggregate shopping list items from all plants with v2 care info
 */
export function useShoppingList(): ShoppingListData {
  const { plants, loading, error } = usePlants();

  const result = useMemo(() => {
    // Map to collect items: normalized name -> ShoppingItem
    const itemMap = new Map<string, ShoppingItem>();

    let plantsWithCareInfo = 0;

    plants.forEach((plant: PlantWithId) => {
      if (!plant.careInfo || !isExpandedCareInfo(plant.careInfo)) {
        return;
      }

      plantsWithCareInfo++;
      const careInfo = plant.careInfo;
      const plantRef = {
        id: plant.id,
        name: plant.nickname || plant.commonName,
      };

      // Extract fertilizer from feeding section
      if (careInfo.feeding?.product) {
        const productName = careInfo.feeding.product;
        const normalized = normalizeForComparison(productName);

        if (itemMap.has(normalized)) {
          const existing = itemMap.get(normalized)!;
          if (!existing.plants.some((p) => p.id === plant.id)) {
            existing.plants.push(plantRef);
          }
        } else {
          itemMap.set(normalized, {
            name: productName,
            category: 'fertilizers',
            plants: [plantRef],
          });
        }
      }

      // Extract soil ingredients from soil recipes
      if (careInfo.soilRecipes && Array.isArray(careInfo.soilRecipes)) {
        careInfo.soilRecipes.forEach((recipe) => {
          if (recipe.ingredients && Array.isArray(recipe.ingredients)) {
            recipe.ingredients.forEach((ingredient) => {
              const { name, category } = parseIngredient(ingredient);
              const normalized = normalizeForComparison(name);

              if (itemMap.has(normalized)) {
                const existing = itemMap.get(normalized)!;
                if (!existing.plants.some((p) => p.id === plant.id)) {
                  existing.plants.push(plantRef);
                }
              } else {
                itemMap.set(normalized, {
                  name,
                  category,
                  plants: [plantRef],
                });
              }
            });
          }
        });
      }
    });

    // Convert map to array and sort by category then by number of plants (descending)
    const items = Array.from(itemMap.values()).sort((a, b) => {
      // Category order: fertilizers, soils, amendments
      const categoryOrder = { fertilizers: 0, soils: 1, amendments: 2 };
      const catDiff = categoryOrder[a.category] - categoryOrder[b.category];
      if (catDiff !== 0) return catDiff;
      // Within category, sort by plant count descending
      return b.plants.length - a.plants.length;
    });

    return {
      items,
      totalProducts: items.length,
      plantsWithCareInfo,
    };
  }, [plants]);

  return {
    ...result,
    loading,
    error,
  };
}
